import { useGetUserQuery } from "features/auth/auth";
import { useAppSelector, useAppDispatch } from "features/store";
import Layout from "../components/Layout";
import {
  Alert,
  CircularProgress,
  Container,
  Box,
  Typography,
  IconButton,
  Button,
  Menu,
  MenuItem,
  TextField,
  Tooltip,
  Breadcrumbs,
  Link,
  FormControl,
  Select,
  SelectChangeEvent,
  ToggleButton,
  ToggleButtonGroup,
  Drawer,
  Divider,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import {
  ArrowBack,
  Home,
  CreateNewFolder,
  Delete,
  DriveFileRenameOutline,
  DriveFileMoveOutlined,
  UploadFile,
  Tune,
  Inventory2,
} from "@mui/icons-material";
import AppDialog from "../components/common/AppDialog";
import FileList from "../components/fileStorage/FileList";
import type { TimeDisplayMode } from "../components/fileStorage/FileItem";
import FileSearch from "../components/fileStorage/FileSearch";
import FolderTypeFilter from "../components/fileStorage/FolderTypeFilter";
import MoveFileDialog from "components/fileStorage/MoveFileDialog";
import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { FileItem, FolderType } from "../types/interfaces/filesStorage";
import {
  useGetFileSystemQuery,
  filesStorageApi,
  useGetFolderDisconnectStatusQuery,
  useCreateBrowserFolderMutation,
  usePresignBrowserUploadMutation,
  useFinalizeBrowserUploadMutation,
  useDeleteBrowserFileMutation,
  useRenameBrowserFileMutation,
  useMoveBrowserFileMutation,
  useDeleteBrowserFolderMutation,
  useRenameBrowserFolderMutation,
  useDisconnectUserFolderMutation,
  useUpdateUserFolderDisplayNameMutation,
  useTopFolderZipReadyBadgesMutation,
  useLazyGetFolderSyncStatusQuery,
} from "features/fileSystem/filesStorage";
import { useDownloadManager } from "../hooks/useDownloadManager";
import {
  isTopDocumentFolderDownloadTarget,
  normPath,
} from "../utils/topFolderDownload";
import { CLIENT_VIEW_READ_ONLY_TOOLTIP, useViewAs } from "hooks/useViewAs";
import { usePermissions } from "hooks/usePermissions";
import { ACTIONS } from "utils/access";
import { dataStorageRights, rowControls } from "utils/dataStorageControls";
import {
  paginationStorage,
  folderMappingsStorage,
} from "../utils/localStorage";
import { TABLE_PAGE_CONTAINER_HEIGHT } from "config";
import { storagePaginationValues } from "../utils/constants";
import {
  isUnchangedRename,
  physicalFolderName,
  remapFolderMappings,
} from "../utils/folderRename";
import {
  detectExtensionFromFileSignature,
  splitFileNameForUpload,
} from "../utils/fileExtension";
import { useSearchParams, useNavigate, useLocation } from "react-router-dom";
import {
  pushNotification,
  removeNotification,
} from "features/notificationSlice";
import { startFolderConnectSync } from "features/folderConnectSyncSlice";
import ConnectFolderDialog from "components/fileStorage/ConnectFolderDialog";
import ReadOnlyTooltip from "components/common/ReadOnlyTooltip";
import { useFilePreview } from "components/preview";

export default function HomePage() {
  const theme = useTheme();
  const dispatch = useAppDispatch();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const webAccessDenied = useAppSelector((state) => state.auth.webAccessDenied);
  const { isClientView: isClientViewMode, profile } = useViewAs();
  const { sees } = usePermissions();
  const { isLoading: userLoading } = useGetUserQuery(undefined, {
    skip: !!webAccessDenied,
  });
  const navigate = useNavigate();
  const location = useLocation();

  const rights = dataStorageRights(sees);
  const {
    organize: canOrganize,
    deleteData: canDeleteData,
    clientFolders: canManageClientFolders,
  } = rights;
  const effectiveMayDownloadTopFolder = sees(
    ACTIONS.DATA_STORAGE_EMPLOYEE_FOLDER,
  );
  const canViewFileTimestamps = profile?.user_type === "internal";
  const isClientViewReadOnlyMode = isClientViewMode;
  const canMutateDataStorage =
    (canOrganize || canDeleteData || canManageClientFolders) &&
    !isClientViewMode;
  const showManageControls = canOrganize;
  const clientViewDisabledTooltip = CLIENT_VIEW_READ_ONLY_TOOLTIP;
  const [searchParams, setSearchParams] = useSearchParams();

  const getInitialState = useCallback(() => {
    const initialPrefixFromState = (location.state as any)?.initialPrefix;
    const prefix = initialPrefixFromState || searchParams.get("prefix") || "";
    const page = parseInt(searchParams.get("page") || "1");
    const search = searchParams.get("search") || null;
    const folderType = (searchParams.get("folder_type") as FolderType) || "all";
    const urlSortField = searchParams.get("sort_field");
    const urlSortOrder = searchParams.get("sort_order");
    const hasSortInUrl = urlSortField && urlSortOrder;
    const sortBy =
      prefix === ""
        ? hasSortInUrl && urlSortField === "name"
          ? `${urlSortField}_${urlSortOrder}`
          : "name_asc"
        : `${urlSortField || "date"}_${urlSortOrder || "desc"}`;
    const timeDisplay =
      (searchParams.get("time_display") as TimeDisplayMode) || "local";

    return { prefix, page, search, folderType, sortBy, timeDisplay };
  }, [searchParams, location.state]);

  const [currentLimit, setCurrentLimit] = useState(() => {
    const savedLimit = paginationStorage.getPaginationValue();
    const validLimit = storagePaginationValues.includes(savedLimit)
      ? savedLimit
      : 25;
    return validLimit;
  });

  const [currentPrefix, setCurrentPrefix] = useState(getInitialState().prefix);
  const [currentSearch, setCurrentSearch] = useState<string | null>(
    getInitialState().search,
  );
  const [currentFolderType, setCurrentFolderType] = useState<FolderType>(
    getInitialState().folderType,
  );
  const [currentContinuationToken, setCurrentContinuationToken] = useState<
    string | null
  >(null);
  const [currentPage, setCurrentPage] = useState(getInitialState().page);
  const [pageTokens, setPageTokens] = useState<string[]>([]);
  const [showFilter, setShowFilter] = useState<boolean>(false);
  const [sortBy, setSortBy] = useState(getInitialState().sortBy);
  const [timeDisplayMode, setTimeDisplayMode] = useState<TimeDisplayMode>(
    getInitialState().timeDisplay,
  );

  const [actionMenuAnchor, setActionMenuAnchor] = useState<HTMLElement | null>(
    null,
  );
  const [actionMenuItem, setActionMenuItem] = useState<FileItem | null>(null);
  const [createFolderDialogOpen, setCreateFolderDialogOpen] = useState(false);
  const [createFolderName, setCreateFolderName] = useState("");
  const [renameDialogOpen, setRenameDialogOpen] = useState(false);
  const [renameValue, setRenameValue] = useState("");
  const [renameSubmitting, setRenameSubmitting] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteInProgress, setDeleteInProgress] = useState(false);
  const [moveDialogOpen, setMoveDialogOpen] = useState(false);
  const [pregenerateZipDialogOpen, setPregenerateZipDialogOpen] =
    useState(false);
  const [moveInProgress, setMoveInProgress] = useState(false);
  const [connectDialogOpen, setConnectDialogOpen] = useState(false);
  const [disconnectPoll, setDisconnectPoll] = useState<{
    jobId: string;
    fname: string;
    fsource: string;
  } | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const prevTopZipJobCountRef = useRef(0);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [uploadTargetPrefix, setUploadTargetPrefix] = useState<string>("");
  const [uploadDraftFiles, setUploadDraftFiles] = useState<
    Array<{
      id: string;
      file: File;
      baseName: string;
      extension: string;
      error?: string;
    }>
  >([]);
  const [uploadDragOver, setUploadDragOver] = useState(false);
  const [uploadInProgress, setUploadInProgress] = useState(false);
  const [uploadTotalCount, setUploadTotalCount] = useState(0);
  const [uploadUploadedCount, setUploadUploadedCount] = useState(0);
  const [mobileFilterDrawerOpen, setMobileFilterDrawerOpen] = useState(false);

  useEffect(() => {
    if (!disconnectPoll?.jobId) return undefined;
    const id = `disconnect-${disconnectPoll.jobId}`;
    dispatch(
      pushNotification({
        id,
        message: disconnectPoll.fname
          ? `Removing folder "${disconnectPoll.fname}" from the platform (cleaning database records)…`
          : "Removing folder from the platform…",
        severity: "info",
        variant: "loading",
        autoHideMs: null,
      }),
    );
    return () => {
      dispatch(removeNotification(id));
    };
  }, [disconnectPoll?.jobId, disconnectPoll?.fname, dispatch]);

  const { openPreview } = useFilePreview();
  const [createBrowserFolder] = useCreateBrowserFolderMutation();
  const [presignBrowserUpload] = usePresignBrowserUploadMutation();
  const [finalizeBrowserUpload] = useFinalizeBrowserUploadMutation();
  const [deleteBrowserFile] = useDeleteBrowserFileMutation();
  const [renameBrowserFile] = useRenameBrowserFileMutation();
  const [moveBrowserFile] = useMoveBrowserFileMutation();
  const [deleteBrowserFolder] = useDeleteBrowserFolderMutation();
  const [renameBrowserFolder] = useRenameBrowserFolderMutation();
  const [disconnectUserFolder] = useDisconnectUserFolderMutation();
  const [updateUserFolderDisplayName] =
    useUpdateUserFolderDisplayNameMutation();
  const [fetchTopFolderZipBadges] = useTopFolderZipReadyBadgesMutation();

  const { data: disconnectPollData, isError: disconnectPollError } =
    useGetFolderDisconnectStatusQuery(
      { job_id: disconnectPoll?.jobId ?? "" },
      {
        skip: !disconnectPoll?.jobId,
        pollingInterval: disconnectPoll?.jobId ? 1500 : 0,
      },
    );

  const updateURL = useCallback(
    (newState: {
      prefix?: string;
      page?: number;
      search?: string | null;
      folderType?: FolderType;
      sortBy?: string;
      timeDisplay?: TimeDisplayMode;
    }) => {
      const currentState = getInitialState();
      const updatedState = { ...currentState, ...newState };

      const params = new URLSearchParams();
      if (updatedState.prefix) params.set("prefix", updatedState.prefix);
      if (updatedState.page > 1)
        params.set("page", updatedState.page.toString());
      if (updatedState.search) params.set("search", updatedState.search);
      if (updatedState.folderType && updatedState.folderType !== "all")
        params.set("folder_type", updatedState.folderType);
      const sortField = updatedState.sortBy?.split("_")[0];
      const sortOrder = updatedState.sortBy?.split("_")[1];
      const isRootNameSort =
        !updatedState.prefix &&
        (updatedState.sortBy === "name_asc" ||
          updatedState.sortBy === "name_desc");
      if (updatedState.sortBy && (updatedState.prefix || isRootNameSort)) {
        params.set("sort_field", sortField!);
        params.set("sort_order", sortOrder!);
      }
      if (updatedState.timeDisplay && updatedState.timeDisplay !== "local")
        params.set("time_display", updatedState.timeDisplay);

      setSearchParams(params);
    },
    [getInitialState, setSearchParams],
  );

  const sortFromUrl = currentPrefix
    ? {
        field: (searchParams.get("sort_field") || "date") as
          | "name"
          | "date"
          | "size",
        order: (searchParams.get("sort_order") || "desc") as "asc" | "desc",
      }
    : null;
  const sortField = sortFromUrl
    ? sortFromUrl.field
    : (sortBy.split("_")[0] as "name" | "date" | "size");
  const sortOrder = sortFromUrl
    ? sortFromUrl.order
    : (sortBy.split("_")[1] as "asc" | "desc");
  const displaySortBy = sortFromUrl
    ? `${sortFromUrl.field}_${sortFromUrl.order}`
    : sortBy;

  const apiSortField =
    !canViewFileTimestamps && sortField === "date" ? "name" : sortField;
  const apiSortOrder =
    !canViewFileTimestamps && sortField === "date" ? "asc" : sortOrder;

  const fileSystemQueryArgs = {
    prefix: currentPrefix,
    continuation_token: currentContinuationToken,
    search: currentSearch,
    limit: currentLimit,
    folder_type: currentFolderType,
    page: currentPage,
    sort_field: apiSortField,
    sort_order: apiSortOrder,
  };

  const {
    data: fileSystemData,
    isLoading: fileSystemLoading,
    error: fileSystemError,
    isFetching: fileSystemFetching,
    refetch: refetchFileSystem,
  } = useGetFileSystemQuery(fileSystemQueryArgs, {
    // Refetch on every navigation so we never serve a stale cached listing
    // (cached data shows instantly while the ~1s refetch runs).
    refetchOnMountOrArgChange: true,
  });

  const [triggerFolderSyncStatus] = useLazyGetFolderSyncStatusQuery();

  const blockTopFolderZipWhileFolderSyncing =
    fileSystemData?.sync_status === "syncing";
  const downloadManager = useDownloadManager({
    blockTopFolderZipWhileFolderSyncing,
  });
  const displayedFolders = useMemo(() => {
    const rows = fileSystemData?.folders ?? [];
    if (apiSortField !== "name") return rows;
    const copy = [...rows];
    copy.sort((a, b) => {
      const cmp = a.fname.localeCompare(b.fname, undefined, {
        sensitivity: "base",
        numeric: true,
      });
      return apiSortOrder === "desc" ? -cmp : cmp;
    });
    return copy;
  }, [fileSystemData?.folders, apiSortField, apiSortOrder]);

  const showPrepareDownloadsUi =
    sees(ACTIONS.DATA_STORAGE_PREPARE_DOWNLOADS) &&
    effectiveMayDownloadTopFolder;

  const topDocumentZipTargetsOnPage = useMemo(() => {
    const root = fileSystemData?.client_folder_prefix;
    if (!root) return [];
    return displayedFolders.filter((f) =>
      isTopDocumentFolderDownloadTarget(f, root),
    );
  }, [displayedFolders, fileSystemData?.client_folder_prefix]);

  const [topFolderZipReadyMap, setTopFolderZipReadyMap] = useState<
    Record<string, boolean>
  >({});

  const topZipTargetsSignature = useMemo(
    () =>
      topDocumentZipTargetsOnPage
        .map((t) =>
          [
            t.fsource,
            t.direct_file_count ?? "",
            t.file_count ?? "",
            t.last_modified ?? "",
            t.has_files_in_subtree ?? "",
          ].join("\x1f"),
        )
        .join("\0"),
    [topDocumentZipTargetsOnPage],
  );

  const refetchTopFolderZipBadges = useCallback(async () => {
    if (!showPrepareDownloadsUi || topDocumentZipTargetsOnPage.length === 0) {
      setTopFolderZipReadyMap({});
      return;
    }
    try {
      const res = await fetchTopFolderZipBadges({
        sources: topDocumentZipTargetsOnPage.map((t) => t.fsource),
        verify_fingerprint: true,
      }).unwrap();
      const raw = res.ready ?? {};
      const merged: Record<string, boolean> = {};
      for (const t of topDocumentZipTargetsOnPage) {
        const k = normPath(t.fsource);
        merged[k] = raw[t.fsource] === true || raw[k] === true;
      }
      setTopFolderZipReadyMap(merged);
    } catch {
      setTopFolderZipReadyMap({});
    }
  }, [
    showPrepareDownloadsUi,
    topDocumentZipTargetsOnPage,
    fetchTopFolderZipBadges,
  ]);

  useEffect(() => {
    void refetchTopFolderZipBadges();
  }, [topZipTargetsSignature, refetchTopFolderZipBadges]);

  const prevFileSystemSyncStatusRef = useRef<string | undefined>();
  useEffect(() => {
    const s = fileSystemData?.sync_status;
    if (
      showPrepareDownloadsUi &&
      prevFileSystemSyncStatusRef.current === "syncing" &&
      s === "ready"
    ) {
      void refetchTopFolderZipBadges();
    }
    prevFileSystemSyncStatusRef.current = s;
  }, [
    fileSystemData?.sync_status,
    showPrepareDownloadsUi,
    refetchTopFolderZipBadges,
  ]);

  const topZipJobCount = useAppSelector(
    (s) => Object.keys(s.topFolderZip.jobs).length,
  );

  useEffect(() => {
    if (!showPrepareDownloadsUi) {
      prevTopZipJobCountRef.current = topZipJobCount;
      return;
    }
    if (prevTopZipJobCountRef.current > topZipJobCount) {
      void refetchTopFolderZipBadges();
    }
    prevTopZipJobCountRef.current = topZipJobCount;
  }, [topZipJobCount, showPrepareDownloadsUi, refetchTopFolderZipBadges]);

  const someTopFolderZipNotCached = useMemo(() => {
    if (!showPrepareDownloadsUi || topDocumentZipTargetsOnPage.length === 0) {
      return false;
    }
    return topDocumentZipTargetsOnPage.some(
      (t) => topFolderZipReadyMap[normPath(t.fsource)] !== true,
    );
  }, [
    showPrepareDownloadsUi,
    topDocumentZipTargetsOnPage,
    topFolderZipReadyMap,
  ]);

  useEffect(() => {
    const state = location.state as any;
    const seed = state?.folderMappingSeed as unknown;
    if (Array.isArray(seed) && seed.length > 0) {
      for (const pair of seed) {
        if (
          Array.isArray(pair) &&
          pair.length === 2 &&
          typeof pair[0] === "string" &&
          typeof pair[1] === "string"
        ) {
          folderMappingsStorage.setFolderMapping(pair[0], pair[1]);
        }
      }
    } else if (state?.initialPrefix && state?.initialFname) {
      folderMappingsStorage.setFolderMapping(
        state.initialFname,
        state.initialPrefix,
      );
    }
  }, [location.state]);

  const validSortOptions = canViewFileTimestamps
    ? [
        "date_desc",
        "date_asc",
        "size_desc",
        "size_asc",
        "name_asc",
        "name_desc",
      ]
    : ["size_desc", "size_asc", "name_asc", "name_desc"];

  const sortSelectValue = (() => {
    const fromState = validSortOptions.includes(sortBy) ? sortBy : null;
    const fromUrl =
      !fileSystemData?.has_direct_files &&
      (displaySortBy === "date_desc" ||
        displaySortBy === "date_asc" ||
        displaySortBy === "size_desc" ||
        displaySortBy === "size_asc")
        ? "name_asc"
        : !canViewFileTimestamps &&
            (displaySortBy === "date_desc" || displaySortBy === "date_asc")
          ? "name_asc"
          : validSortOptions.includes(displaySortBy)
            ? displaySortBy
            : "name_asc";
    return fromState ?? fromUrl;
  })();

  useEffect(() => {
    if (fileSystemData?.sync_status !== "syncing") return;
    const interval = setInterval(() => refetchFileSystem(), 3000);
    return () => clearInterval(interval);
  }, [fileSystemData?.sync_status, refetchFileSystem]);

  // Non-blocking "new data arrived" detector for webhook uploads: poll data_updated_at
  // and, when it advances, show a soft notification + refetch (without hiding the list).
  const lastSeenDataUpdatedAtRef = useRef<string | null | undefined>(undefined);
  useEffect(() => {
    lastSeenDataUpdatedAtRef.current = fileSystemData?.data_updated_at ?? null;
  }, [fileSystemData?.data_updated_at, fileSystemData?.user_folder_id]);

  const currentFolderId = fileSystemData?.user_folder_id ?? null;
  useEffect(() => {
    if (!currentFolderId) return;
    if (fileSystemData?.sync_status === "syncing") return; // full sync owns the UI
    const interval = setInterval(async () => {
      try {
        const res = await triggerFolderSyncStatus(currentFolderId).unwrap();
        if (res.sync_status === "syncing") return;
        const incoming = res.data_updated_at ?? null;
        const baseline = lastSeenDataUpdatedAtRef.current ?? null;
        const incomingMs = incoming ? Date.parse(incoming) : NaN;
        const baselineMs = baseline ? Date.parse(baseline) : NaN;
        if (
          !Number.isNaN(incomingMs) &&
          (Number.isNaN(baselineMs) || incomingMs > baselineMs)
        ) {
          lastSeenDataUpdatedAtRef.current = incoming; // optimistic: avoid double-notify
          dispatch(
            pushNotification({
              message: "New data arrived — refreshing the folder…",
              severity: "info",
            }),
          );
          refetchFileSystem();
        }
      } catch {
        // ignore transient heartbeat errors
      }
    }, 12000);
    return () => clearInterval(interval);
  }, [
    currentFolderId,
    fileSystemData?.sync_status,
    triggerFolderSyncStatus,
    refetchFileSystem,
    dispatch,
  ]);

  useEffect(() => {
    if (userLoading) return;
    if (
      !canViewFileTimestamps &&
      (sortField === "date" || sortBy === "date_asc" || sortBy === "date_desc")
    ) {
      setSortBy("name_asc");
      updateURL({ sortBy: "name_asc" });
    }
  }, [userLoading, canViewFileTimestamps, sortField, sortBy, updateURL]);

  const isDbBacked = !!fileSystemData?.total_pages;

  useEffect(() => {
    if (
      fileSystemData?.show_filter !== null &&
      fileSystemData?.show_filter !== undefined
    ) {
      setShowFilter(fileSystemData.show_filter);
    }
  }, [fileSystemData?.show_filter]);

  useEffect(() => {
    const effectiveSort = currentPrefix ? displaySortBy : sortBy;
    const sortRequiresFiles =
      effectiveSort === "date_desc" ||
      effectiveSort === "date_asc" ||
      effectiveSort === "size_desc" ||
      effectiveSort === "size_asc";
    if (
      isDbBacked &&
      fileSystemData?.has_direct_files === false &&
      sortRequiresFiles
    ) {
      setSortBy("name_asc");
      setCurrentPage(1);
      setPageTokens([]);
      updateURL({ page: 1, sortBy: "name_asc" });
    }
  }, [
    isDbBacked,
    fileSystemData?.has_direct_files,
    currentPrefix,
    displaySortBy,
    sortBy,
  ]);

  useEffect(() => {
    if (
      fileSystemData &&
      !fileSystemData.continuation_token &&
      currentPage > 1
    ) {
    }
  }, [fileSystemData?.continuation_token, currentPage]);

  const handleItemClick = (item: FileItem) => {
    if (item.ftype === "folder") {
      const originalItem = fileSystemData?.folders.find(
        (folder) => folder.fsource === item.fsource,
      );
      const fnameToStore = originalItem?.fname || item.fname;

      folderMappingsStorage.clearIrrelevantMappings(item.fsource);
      folderMappingsStorage.setFolderMapping(fnameToStore, item.fsource);

      setCurrentPrefix(item.fsource);
      setCurrentContinuationToken(null);
      setCurrentPage(1);
      setPageTokens([]);
      updateURL({ prefix: item.fsource, page: 1, search: null });
    }
  };

  const handlePreviewClick = (item: FileItem) => {
    const originalItem =
      fileSystemData?.folders.find(
        (folder) => folder.fsource === item.fsource,
      ) || item;
    openPreview(originalItem);
  };

  const showNotification = (
    message: string,
    severity: "success" | "error" | "info" | "warning",
  ) => {
    dispatch(pushNotification({ message, severity }));
  };

  const patchCurrentFileSystem = useCallback(
    (recipe: (draft: any) => void) => {
      return dispatch(
        filesStorageApi.util.updateQueryData(
          "getFileSystem",
          fileSystemQueryArgs,
          (draft) => recipe(draft),
        ),
      );
    },
    [dispatch, fileSystemQueryArgs],
  );

  const handleDownloadClick = async (item: FileItem) => {
    const originalItem =
      fileSystemData?.folders.find(
        (folder) => folder.fsource === item.fsource,
      ) || item;

    const clientRoot = fileSystemData?.client_folder_prefix;
    const isTop =
      originalItem.ftype === "folder" &&
      isTopDocumentFolderDownloadTarget(originalItem, clientRoot);

    if (isTop) {
      if (!effectiveMayDownloadTopFolder) {
        showNotification(
          "You do not have permission to download this folder.",
          "error",
        );
        return;
      }
      const r = await downloadManager.downloadTopFolder(originalItem);
      if (!r.success && r.message) {
        showNotification(
          r.message,
          r.notificationSeverity === "info" ? "info" : "error",
        );
      }
      return;
    }

    const result = await downloadManager.downloadItem(originalItem);

    if (result.success) {
      const lower = result.message.toLowerCase();
      if (!lower.includes("building") && !lower.includes("generating")) {
        showNotification(result.message, "success");
      }
    } else {
      showNotification(result.message, "error");
    }
  };

  const handleGenerateTopFolderZipFromMenu = async () => {
    if (isClientViewReadOnlyMode) return;
    const item = actionMenuItem;
    if (fileSystemData?.sync_status === "syncing") {
      handleCloseActionMenu();
      showNotification(
        "Wait until folder sync finishes before preparing downloads.",
        "info",
      );
      return;
    }
    handleCloseActionMenu();
    if (!item || item.ftype !== "folder") return;
    const r = await downloadManager.generateTopFolderZip(item);
    void refetchTopFolderZipBadges();
    if (r.success) {
      showNotification(r.message, r.notificationSeverity ?? "success");
    } else if (r.message) {
      showNotification(
        r.message,
        r.notificationSeverity === "info" ? "info" : "error",
      );
    }
  };

  const handleConfirmBulkPregenerateTopZips = async () => {
    setPregenerateZipDialogOpen(false);
    const root = fileSystemData?.client_folder_prefix;
    if (!root || !showPrepareDownloadsUi) return;
    if (fileSystemData?.sync_status === "syncing") {
      showNotification(
        "Wait until folder sync finishes before preparing downloads.",
        "info",
      );
      return;
    }
    if (topZipJobCount > 0) {
      showNotification(
        "Wait until the current downloads finish preparing before starting another batch.",
        "info",
      );
      return;
    }
    const targets = displayedFolders
      .filter((f) => isTopDocumentFolderDownloadTarget(f, root))
      .filter((f) => topFolderZipReadyMap[normPath(f.fsource)] !== true);
    if (targets.length === 0) {
      showNotification(
        "All listed folders are already prepared for download.",
        "info",
      );
      return;
    }
    const { started, already, failed } =
      await downloadManager.generateTopFolderZipBatch(targets, {
        quiet: true,
      });
    const sev: "success" | "warning" = failed > 0 ? "warning" : "success";
    dispatch(
      pushNotification({
        message: `Prepare Downloads — ${started} job(s) started, ${already} already prepared${
          failed ? `, ${failed} failed` : ""
        }.`,
        severity: sev,
      }),
    );
    void refetchTopFolderZipBadges();
  };

  const removeFolderMappingBySource = useCallback((source: string) => {
    const mappings = folderMappingsStorage.getFolderMappings();
    const cleaned = Object.fromEntries(
      Object.entries(mappings).filter(
        ([, storedSource]) => storedSource !== source,
      ),
    );
    folderMappingsStorage.setFolderMappings(cleaned);
  }, []);

  const handleOpenActionMenu = (item: FileItem, event: React.MouseEvent) => {
    if (isClientViewReadOnlyMode) return;
    setActionMenuItem(item);
    setActionMenuAnchor(event.currentTarget as HTMLElement);
  };

  const handleCloseActionMenu = () => {
    setActionMenuAnchor(null);
  };

  const handleCreateFolderClick = () => {
    if (!canMutateDataStorage || !currentPrefix) return;
    setCreateFolderName("");
    setCreateFolderDialogOpen(true);
  };

  const handleCreateFolderDialogClose = () => {
    setCreateFolderDialogOpen(false);
  };

  const handleCreateFolderConfirm = async () => {
    if (!canMutateDataStorage || !currentPrefix) return;

    const folderName = createFolderName.trim();
    if (!folderName) {
      handleCreateFolderDialogClose();
      return;
    }
    if (folderName.includes("/")) {
      showNotification("Folder name cannot contain '/'", "error");
      return;
    }
    if (folderName === "." || folderName === "..") {
      showNotification("Invalid folder name", "error");
      return;
    }
    const existingFolder = fileSystemData?.folders?.find(
      (item) => item.ftype === "folder" && item.fname === folderName,
    );
    if (existingFolder) {
      showNotification(
        `A folder named '${folderName}' already exists in this location`,
        "error",
      );
      return;
    }

    const folderPath = `${currentPrefix}/${folderName}`;
    const optimisticItem: FileItem = {
      fname: folderName,
      ftype: "folder",
      fsource: folderPath,
      subfolder_count: 0,
      direct_file_count: 0,
      is_creating: true,
    };

    handleCreateFolderDialogClose();

    const patch = patchCurrentFileSystem((draft: any) => {
      if (!draft?.folders) return;
      draft.folders = [
        optimisticItem,
        ...draft.folders.filter(
          (item: FileItem) => item.fsource !== folderPath,
        ),
      ];
      if (typeof draft.total_count === "number") {
        draft.total_count += 1;
      }
    });

    try {
      await createBrowserFolder({
        prefix: currentPrefix,
        folder_name: folderName,
      }).unwrap();
      folderMappingsStorage.setFolderMapping(folderName, folderPath);
      showNotification(`Created folder ${folderName}`, "success");
    } catch (error: unknown) {
      patch.undo();
      const msg =
        (error as { data?: { error?: string } })?.data?.error ||
        "Failed to create folder";
      showNotification(msg, "error");
    }
  };

  const formatBytes = (bytes: number) => {
    if (!Number.isFinite(bytes) || bytes < 0) return "0 B";
    if (bytes < 1024) return `${bytes} B`;
    const kb = bytes / 1024;
    if (kb < 1024) return `${kb.toFixed(1)} KB`;
    const mb = kb / 1024;
    if (mb < 1024) return `${mb.toFixed(2)} MB`;
    const gb = mb / 1024;
    return `${gb.toFixed(2)} GB`;
  };

  const buildUploadFileName = (draft: {
    baseName: string;
    extension: string;
  }) => `${(draft.baseName || "").trim()}${draft.extension || ""}`;

  const validateUploadDrafts = <
    T extends { baseName: string; extension: string; error?: string },
  >(
    drafts: T[],
  ) => {
    const duplicateMsg =
      "A file with this name already exists. Please change the name and upload the file again.";
    const draftDuplicateMsg =
      "Two or more selected files have the same name. Please make them unique.";
    const existingNames = new Set(
      (fileSystemData?.folders ?? [])
        .filter((item) => item.ftype === "file")
        .map((item) => item.fname),
    );

    const nameCounts = new Map<string, number>();
    for (const item of drafts) {
      const finalName = buildUploadFileName(item);
      if (!finalName) continue;
      nameCounts.set(finalName, (nameCounts.get(finalName) || 0) + 1);
    }

    return drafts.map((item) => {
      const desiredBaseName = (item.baseName || "").trim();
      if (!desiredBaseName) {
        return { ...item, error: "File name is required." };
      }
      if (desiredBaseName.includes("/")) {
        return { ...item, error: "File name cannot contain '/'." };
      }
      const desiredFinalName = buildUploadFileName(item);
      if (existingNames.has(desiredFinalName)) {
        return { ...item, error: duplicateMsg };
      }
      if ((nameCounts.get(desiredFinalName) || 0) > 1) {
        return { ...item, error: draftDuplicateMsg };
      }
      return { ...item, error: undefined };
    });
  };

  const addFilesToUploadDraft = async (incoming: File[]) => {
    if (!canMutateDataStorage || !uploadTargetPrefix) return;
    if (!incoming.length) return;

    const preparedIncoming = await Promise.all(
      incoming.map(async (file) => {
        const parsed = splitFileNameForUpload(file.name, file.type);
        const inferredExtension =
          parsed.extension || (await detectExtensionFromFileSignature(file));
        return {
          id: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
          file,
          baseName: parsed.baseName,
          extension: inferredExtension,
        };
      }),
    );

    setUploadDraftFiles((prev) => {
      const next = [...prev, ...preparedIncoming];
      return validateUploadDrafts(next);
    });
  };

  const handleUploadInputChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const files = Array.from(event.target.files || []);
    event.target.value = "";
    if (!files.length) return;
    if (uploadInProgress) return;
    void addFilesToUploadDraft(files);
  };

  const uploadFiles = async (files: File[], targetPrefix: string) => {
    if (!canMutateDataStorage || !targetPrefix) return;
    if (!files.length) return;

    if (files.some((f) => f.name.includes("/"))) {
      showNotification("File name cannot contain '/'", "error");
      return;
    }

    const existingNames = new Set(
      (fileSystemData?.folders ?? [])
        .filter((item) => item.ftype === "file")
        .map((item) => item.fname),
    );

    const duplicates = files.filter((f) => existingNames.has(f.name));
    if (duplicates.length > 0) {
      const names = duplicates.map((f) => f.name).join(", ");
      showNotification(
        `Skipped - already exist in this folder: ${names}`,
        "info",
      );
    }

    const filesToUpload = files.filter((f) => !existingNames.has(f.name));
    for (const file of filesToUpload) {
      existingNames.add(file.name);
      const tempId = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
      const tempSource = `__uploading__/${tempId}/${file.name}`;
      const optimisticItem: FileItem = {
        fname: file.name,
        ftype: "file",
        fsource: tempSource,
        file_size: file.size,
        last_modified: new Date().toISOString(),
        is_uploading: true,
      };
      const finalSource = `${targetPrefix}/${file.name}`;

      const patch = patchCurrentFileSystem((draft: any) => {
        if (!draft?.folders) return;
        draft.folders = [
          optimisticItem,
          ...draft.folders.filter(
            (item: FileItem) =>
              item.fsource !== tempSource && item.fsource !== finalSource,
          ),
        ];
        if (typeof draft.total_count === "number") {
          draft.total_count += 1;
        }
      });

      try {
        const presign = await presignBrowserUpload({
          prefix: targetPrefix,
          file_name: file.name,
          content_type: file.type || "application/octet-stream",
        }).unwrap();

        const uploadResponse = await fetch(presign.upload_url, {
          method: "PUT",
          headers: presign.headers,
          body: file,
        });

        if (!uploadResponse.ok) {
          throw new Error(`Upload failed with status ${uploadResponse.status}`);
        }

        const finalized = await finalizeBrowserUpload({
          prefix: targetPrefix,
          file_name: file.name,
          file_size: file.size,
          content_type: file.type || "application/octet-stream",
        }).unwrap();

        patchCurrentFileSystem((draft: any) => {
          if (!draft?.folders) return;
          draft.folders = draft.folders.map((item: FileItem) =>
            item.fsource === tempSource
              ? { ...finalized.item, is_uploading: false }
              : item,
          );
        });

        showNotification(`Uploaded ${file.name}`, "success");
      } catch (error: unknown) {
        patch.undo();
        const msg =
          (error as { data?: { error?: string } })?.data?.error ||
          `Failed to upload ${file.name}`;
        showNotification(msg, "error");
      } finally {
        setUploadUploadedCount((n) => n + 1);
      }
    }
  };

  const handleConfirmUpload = async () => {
    const readyFiles = uploadDraftFiles
      .filter((d) => !d.error)
      .map((d) => {
        const finalName = buildUploadFileName(d);
        return finalName === d.file.name
          ? d.file
          : new File([d.file], finalName, {
              type: d.file.type || "application/octet-stream",
              lastModified: d.file.lastModified,
            });
      });
    if (!readyFiles.length) {
      showNotification("No valid files to upload", "info");
      return;
    }

    setUploadInProgress(true);
    setUploadTotalCount(readyFiles.length);
    setUploadUploadedCount(0);
    setUploadDialogOpen(false);
    setUploadDraftFiles([]);
    setUploadDragOver(false);
    try {
      await uploadFiles(readyFiles, uploadTargetPrefix);
    } finally {
      setUploadInProgress(false);
      setUploadTotalCount(0);
      setUploadUploadedCount(0);
    }
  };

  const handleRenameDialogOpen = () => {
    if (isClientViewReadOnlyMode) return;
    if (!actionMenuItem) return;
    setActionMenuAnchor(null);
    const item = actionMenuItem;
    if (
      !currentPrefix &&
      item.ftype === "folder" &&
      item.editable_folder_name != null
    ) {
      setRenameValue(item.editable_folder_name);
    } else {
      setRenameValue(item.fname);
    }
    setRenameDialogOpen(true);
  };

  const isRootFolderRenameDialog =
    renameDialogOpen &&
    !currentPrefix &&
    actionMenuItem?.ftype === "folder" &&
    typeof actionMenuItem.user_folder_id === "number" &&
    !actionMenuItem.is_integration;

  // The folders of this listing are employee folders of a bot job: renaming one
  // moves its files in storage (the server decides; the listing says so).
  const renameMovesStorage =
    Boolean(currentPrefix) &&
    actionMenuItem?.ftype === "folder" &&
    Boolean(fileSystemData?.rename_moves_storage);

  const handleRenameDialogClose = () => {
    if (renameSubmitting) return;
    setRenameDialogOpen(false);
    setActionMenuAnchor(null);
    setActionMenuItem(null);
  };

  const handleRenameConfirm = async () => {
    if (!canMutateDataStorage || isClientViewReadOnlyMode) return;
    if (!actionMenuItem) return;

    const item = actionMenuItem;
    const newName = renameValue.trim();

    const rootFolderId =
      !currentPrefix &&
      item.ftype === "folder" &&
      typeof item.user_folder_id === "number"
        ? item.user_folder_id
        : undefined;

    if (rootFolderId != null) {
      const prevName = (item.editable_folder_name ?? item.fname).trim();
      if (!newName || newName === prevName) {
        handleRenameDialogClose();
        return;
      }
      if (newName.includes("/")) {
        showNotification("Folder name cannot contain '/'", "error");
        return;
      }
      if (newName === "." || newName === "..") {
        showNotification("Invalid folder name", "error");
        return;
      }
      try {
        await updateUserFolderDisplayName({
          folder_id: rootFolderId,
          folder: newName,
        }).unwrap();
        const displayName = (item.root_list_owner_prefix || "") + newName;
        showNotification(`Renamed folder to ${displayName}`, "success");
      } catch (error: unknown) {
        const errData = (error as { data?: Record<string, unknown> })?.data;
        const folderField = errData?.folder;
        const msg =
          (typeof errData?.error === "string" && errData.error) ||
          (Array.isArray(folderField)
            ? (folderField as string[]).join(", ")
            : "") ||
          "Failed to rename folder";
        showNotification(msg, "error");
      }
      handleRenameDialogClose();
      handleCloseActionMenu();
      return;
    }

    const movesStorage = item.ftype === "folder" && renameMovesStorage;
    if (isUnchangedRename(item, newName, movesStorage)) {
      handleRenameDialogClose();
      return;
    }
    if (newName.includes("/")) {
      showNotification(
        item.ftype === "folder"
          ? "Folder name cannot contain '/'"
          : "File name cannot contain '/'",
        "error",
      );
      return;
    }
    if (item.ftype === "folder" && (newName === "." || newName === "..")) {
      showNotification("Invalid folder name", "error");
      return;
    }

    if (movesStorage) {
      // No optimistic patch: the folder gets a new path, and the listing shows
      // it once the server has moved it.
      setRenameSubmitting(true);
      try {
        const result = await renameBrowserFolder({
          prefix: item.fsource,
          parent_prefix: currentPrefix,
          new_name: newName,
        }).unwrap();
        const renamedTo = result.item?.fname || newName;
        folderMappingsStorage.setFolderMappings(
          remapFolderMappings(
            folderMappingsStorage.getFolderMappings(),
            item.fsource,
            result.item?.fsource || item.fsource,
            renamedTo,
          ),
        );
        const moved = result.moved_files ?? 0;
        showNotification(
          moved > 0
            ? `Renamed folder to ${renamedTo}; ${moved} file(s) moved in storage`
            : `Renamed folder to ${renamedTo}`,
          "success",
        );
        (result.warnings ?? []).forEach((warning) =>
          showNotification(warning, "warning"),
        );
      } catch (error: unknown) {
        const status = (error as { status?: number | string })?.status;
        if (
          status === 504 ||
          status === "FETCH_ERROR" ||
          status === "TIMEOUT_ERROR"
        ) {
          // The server keeps going after the gateway gives up on the request.
          showNotification(
            "The rename is still finishing in storage; the folder list refreshes in a few seconds.",
            "info",
          );
          window.setTimeout(() => refetchFileSystem(), 5000);
        } else {
          const msg =
            (error as { data?: { error?: string } })?.data?.error ||
            "Failed to rename folder";
          showNotification(msg, "error");
        }
      } finally {
        setRenameSubmitting(false);
      }
      setRenameDialogOpen(false);
      setActionMenuItem(null);
      handleCloseActionMenu();
      return;
    }

    if (item.ftype === "folder") {
      const patch = patchCurrentFileSystem((draft: any) => {
        if (!draft?.folders) return;
        draft.folders = draft.folders.map((current: FileItem) =>
          current.fsource === item.fsource
            ? { ...current, fname: newName }
            : current,
        );
      });

      try {
        await renameBrowserFolder({
          prefix: item.fsource,
          parent_prefix: currentPrefix,
          new_name: newName,
        }).unwrap();
        removeFolderMappingBySource(item.fsource);
        folderMappingsStorage.setFolderMapping(newName, item.fsource);
        showNotification(`Renamed folder to ${newName}`, "success");
      } catch (error: unknown) {
        patch.undo();
        const msg =
          (error as { data?: { error?: string } })?.data?.error ||
          "Failed to rename folder";
        showNotification(msg, "error");
      }
    } else {
      const patch = patchCurrentFileSystem((draft: any) => {
        if (!draft?.folders) return;
        draft.folders = draft.folders.map((current: FileItem) =>
          current.fsource === item.fsource
            ? { ...current, fname: newName }
            : current,
        );
      });

      try {
        await renameBrowserFile({
          prefix: currentPrefix,
          source: item.fsource,
          new_name: newName,
        }).unwrap();
        showNotification(`Renamed file to ${newName}`, "success");
      } catch (error: unknown) {
        patch.undo();
        const msg =
          (error as { data?: { error?: string } })?.data?.error ||
          "Failed to rename file";
        showNotification(msg, "error");
      }
    }

    handleRenameDialogClose();
    handleCloseActionMenu();
  };

  const handleDeleteDialogOpen = () => {
    if (isClientViewReadOnlyMode) return;
    if (!actionMenuItem) return;
    setActionMenuAnchor(null);
    setDeleteDialogOpen(true);
  };

  const handleDeleteDialogClose = () => {
    setDeleteDialogOpen(false);
  };

  const handleDeleteSelected = async () => {
    if (!canMutateDataStorage || isClientViewReadOnlyMode) return;
    if (!actionMenuItem) return;

    const item = actionMenuItem;
    setDeleteInProgress(true);
    setDeleteDialogOpen(false);
    handleCloseActionMenu();

    try {
      const rootFolderId =
        !currentPrefix &&
        item.ftype === "folder" &&
        typeof item.user_folder_id === "number"
          ? item.user_folder_id
          : undefined;

      if (rootFolderId != null) {
        try {
          const res = await disconnectUserFolder({
            folder_id: rootFolderId,
          }).unwrap();
          if (res.queued && res.job_id) {
            setDisconnectPoll({
              jobId: res.job_id,
              fname: item.fname,
              fsource: item.fsource,
            });
          }
        } catch (error: unknown) {
          const msg =
            (error as { data?: { error?: string } })?.data?.error ||
            "Failed to remove folder";
          showNotification(msg, "error");
        }
        handleDeleteDialogClose();
        handleCloseActionMenu();
        return;
      }

      if (item.ftype === "folder") {
        const patch = patchCurrentFileSystem((draft: any) => {
          if (!draft?.folders) return;
          draft.folders = draft.folders.filter(
            (current: FileItem) => current.fsource !== item.fsource,
          );
          if (typeof draft.total_count === "number") {
            draft.total_count = Math.max(0, draft.total_count - 1);
          }
        });

        try {
          await deleteBrowserFolder({
            prefix: item.fsource,
            parent_prefix: currentPrefix,
          }).unwrap();
          removeFolderMappingBySource(item.fsource);
          showNotification(`Deleted folder ${item.fname}`, "success");
        } catch (error) {
          patch.undo();
          showNotification("Failed to delete folder", "error");
        }
      } else {
        const patch = patchCurrentFileSystem((draft: any) => {
          if (!draft?.folders) return;
          draft.folders = draft.folders.filter(
            (current: FileItem) => current.fsource !== item.fsource,
          );
          if (typeof draft.total_count === "number") {
            draft.total_count = Math.max(0, draft.total_count - 1);
          }
        });

        try {
          await deleteBrowserFile({
            prefix: currentPrefix,
            source: item.fsource,
          }).unwrap();
          showNotification(`Deleted file ${item.fname}`, "success");
        } catch (error) {
          patch.undo();
          showNotification("Failed to delete file", "error");
        }
      }

      handleDeleteDialogClose();
      handleCloseActionMenu();
    } finally {
      setDeleteInProgress(false);
    }
  };

  const handleMoveDialogOpen = () => {
    if (isClientViewReadOnlyMode) return;
    if (!actionMenuItem || actionMenuItem.ftype !== "file" || !currentPrefix)
      return;
    setActionMenuAnchor(null);
    setMoveDialogOpen(true);
  };

  const handleMoveDialogClose = () => {
    setMoveDialogOpen(false);
    handleCloseActionMenu();
  };

  const handleMoveHere = async (destinationPrefix: string) => {
    if (!canMutateDataStorage || isClientViewReadOnlyMode) return;
    if (!actionMenuItem || actionMenuItem.ftype !== "file") return;
    const item = actionMenuItem;
    const source = item.fsource;
    setMoveInProgress(true);
    setMoveDialogOpen(false);
    handleCloseActionMenu();

    try {
      await moveBrowserFile({
        source,
        destination_prefix: destinationPrefix,
        prefix: currentPrefix,
      }).unwrap();
      showNotification(`Moved "${item.fname}"`, "success");
    } catch (error: unknown) {
      const msg =
        (error as { data?: { error?: string } })?.data?.error ||
        "Failed to move file";
      showNotification(msg, "error");
    } finally {
      setMoveInProgress(false);
    }
  };

  const handleUploadButtonClick = () => {
    if (!canMutateDataStorage || !currentPrefix) return;
    setUploadTargetPrefix(currentPrefix);
    setUploadDraftFiles([]);
    setUploadDialogOpen(true);
  };

  const handleOpenConnectDialog = () => {
    if (isClientViewReadOnlyMode) return;
    if (!canManageClientFolders) return;
    setConnectDialogOpen(true);
  };

  const handleCloseConnectDialog = () => {
    setConnectDialogOpen(false);
  };

  useEffect(() => {
    if (!disconnectPoll) return;
    if (disconnectPollError) {
      showNotification("Failed to check folder removal status", "error");
      setDisconnectPoll(null);
      return;
    }
    if (!disconnectPollData) return;
    const st = disconnectPollData.status;
    if (st === "completed") {
      removeFolderMappingBySource(disconnectPoll.fsource);
      dispatch(
        filesStorageApi.util.invalidateTags([
          { type: "FileSystem", id: "__root__" },
        ]),
      );
      showNotification(
        `Removed "${disconnectPoll.fname}" from the platform. Data in S3 was not deleted.`,
        "success",
      );
      setDisconnectPoll(null);
    } else if (st === "failed") {
      showNotification(
        disconnectPollData.error_message || "Failed to remove folder",
        "error",
      );
      setDisconnectPoll(null);
    }
  }, [
    disconnectPoll,
    disconnectPollData,
    disconnectPollError,
    dispatch,
    removeFolderMappingBySource,
    showNotification,
  ]);

  const handleLimitChange = (limit: number) => {
    const saved = paginationStorage.setValidPaginationValue(
      limit,
      storagePaginationValues,
    );
    if (!saved) {
    }

    setCurrentLimit(limit);
    setCurrentPage(1);
    setCurrentContinuationToken(null);
    setPageTokens([]);
    updateURL({ page: 1 });
  };

  const handleSortChange = (next: string) => {
    setSortBy(next);
    setCurrentPage(1);
    setCurrentContinuationToken(null);
    setPageTokens([]);
    updateURL({ page: 1, sortBy: next });
  };

  const handlePageChange = (page: number) => {
    if (isDbBacked) {
      if (page < 1 || page > (fileSystemData?.total_pages || 1)) {
        return;
      }
      setCurrentPage(page);
      updateURL({ page });
      return;
    }

    const maxPages = fileSystemData?.continuation_token
      ? pageTokens.length + 1
      : currentPage;

    if (page < 1 || page > maxPages) {
      return;
    }

    if (page === 1) {
      setCurrentContinuationToken(null);
    } else if (page <= pageTokens.length + 1) {
      const tokenIndex = page - 2;
      setCurrentContinuationToken(pageTokens[tokenIndex]);
    } else {
      return;
    }

    setCurrentPage(page);
    updateURL({ page });
  };

  const handleSearch = useCallback(
    (searchTerm: string | null) => {
      setCurrentSearch(searchTerm);
      setCurrentPage(1);
      setCurrentContinuationToken(null);
      setPageTokens([]);
      updateURL({ search: searchTerm, page: 1 });
    },
    [updateURL],
  );

  const handleFolderTypeChange = useCallback(
    (folderType: FolderType) => {
      setCurrentFolderType(folderType);
      setCurrentPage(1);
      setCurrentContinuationToken(null);
      setPageTokens([]);
      updateURL({ folderType, page: 1 });
    },
    [updateURL],
  );

  useEffect(() => {
    if (isDbBacked) return;

    if (fileSystemData?.continuation_token && currentPage > 0) {
      setPageTokens((prev) => {
        const newTokens = [...prev];
        if (currentPage > newTokens.length) {
          newTokens.push(fileSystemData.continuation_token!);
        } else {
          newTokens[currentPage - 1] = fileSystemData.continuation_token!;
        }
        return newTokens;
      });
    } else if (!fileSystemData?.continuation_token && currentPage > 0) {
      setPageTokens((prev) => prev.slice(0, currentPage - 1));
    }
  }, [fileSystemData?.continuation_token, currentPage, isDbBacked]);

  useEffect(() => {
    const currentState = getInitialState();

    if (currentState.prefix !== currentPrefix) {
      setCurrentPrefix(currentState.prefix);
      setCurrentContinuationToken(null);
      setCurrentPage(1);
      setPageTokens([]);
    }

    if (currentState.page !== currentPage) {
      setCurrentPage(currentState.page);
    }

    if (currentState.search !== currentSearch) {
      setCurrentSearch(currentState.search);
      setCurrentPage(1);
      setCurrentContinuationToken(null);
      setPageTokens([]);
    }

    if (currentState.folderType !== currentFolderType) {
      setCurrentFolderType(currentState.folderType);
      setCurrentPage(1);
      setCurrentContinuationToken(null);
      setPageTokens([]);
    }

    if (currentState.sortBy !== sortBy) {
      setSortBy(currentState.sortBy);
      setCurrentPage(1);
      setCurrentContinuationToken(null);
      setPageTokens([]);
    }

    if (currentState.timeDisplay !== timeDisplayMode) {
      setTimeDisplayMode(currentState.timeDisplay);
    }
  }, [
    searchParams,
    getInitialState,
    currentPrefix,
    currentPage,
    currentSearch,
    currentFolderType,
    sortBy,
    timeDisplayMode,
  ]);

  const handleGoBack = () => {
    if (currentPrefix) {
      const pathParts = currentPrefix.split("/").filter((part: string) => part);
      pathParts.pop();
      const newPrefix = pathParts.length > 0 ? pathParts.join("/") : "";

      const hasValidMapping = folderMappingsStorage.getFname(newPrefix);

      if (!hasValidMapping && newPrefix !== "") {
        folderMappingsStorage.clearMappingsForHigherLevel("");
        setCurrentPrefix("");
        setCurrentContinuationToken(null);
        setCurrentPage(1);
        setPageTokens([]);
        setSortBy("name_asc");
        setCurrentFolderType("all");
        updateURL({ prefix: "", page: 1, folderType: "all" });
      } else {
        folderMappingsStorage.clearMappingsForHigherLevel(newPrefix);
        setCurrentPrefix(newPrefix);
        setCurrentContinuationToken(null);
        setCurrentPage(1);
        setPageTokens([]);
        updateURL({ prefix: newPrefix, page: 1 });
      }
    } else {
      navigate(-1);
    }
  };

  const buildBreadcrumbs = useCallback(() => {
    const breadcrumbs = [];

    breadcrumbs.push({
      fname: "Home",
      fsource: "",
      isClickable: currentPrefix,
    });

    if (!currentPrefix) {
      return breadcrumbs;
    }

    const pathParts = currentPrefix.split("/").filter((part: string) => part);

    let currentPath = "";
    for (let i = 0; i < pathParts.length; i++) {
      currentPath += (currentPath ? "/" : "") + pathParts[i];
      const fname = folderMappingsStorage.getFname(currentPath);
      if (!fname) {
        continue;
      }
      const isLast = i === pathParts.length - 1;

      breadcrumbs.push({
        fname,
        fsource: currentPath,
        isClickable: !isLast,
      });
    }

    return breadcrumbs;
  }, [currentPrefix]);

  const handleBreadcrumbClick = (fsource: string) => {
    if (fsource === currentPrefix) {
      return;
    }

    try {
      folderMappingsStorage.clearMappingsForHigherLevel(fsource);

      setCurrentPrefix(fsource);
      setCurrentContinuationToken(null);
      setCurrentPage(1);
      setPageTokens([]);
      if (fsource === "") {
        setSortBy("name_asc");
        setCurrentFolderType("all");
        updateURL({ prefix: "", page: 1, folderType: "all" });
      } else {
        updateURL({ prefix: fsource, page: 1 });
      }
    } catch (error) {
      setCurrentPrefix("");
      setCurrentContinuationToken(null);
      setCurrentPage(1);
      setPageTokens([]);
      setSortBy("name_asc");
      setCurrentFolderType("all");
      updateURL({ prefix: "", page: 1, folderType: "all" });
    }
  };

  if (userLoading) {
    return (
      <Layout title="File System" content="File System" noScroll>
        <Container
          component="main"
          maxWidth={isMobile ? false : "lg"}
          disableGutters={isMobile}
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: "100%",
            boxSizing: "border-box",
            height: TABLE_PAGE_CONTAINER_HEIGHT,
            py: 3,
            px: { xs: 1.5, sm: 3 },
          }}
        >
          <CircularProgress />
        </Container>
      </Layout>
    );
  }

  const isFolderSyncing =
    !!currentPrefix && fileSystemData?.sync_status === "syncing";

  // At the root the row menu holds the client-folder controls, inside a folder
  // the data controls.
  const showRowMenu = currentPrefix
    ? canOrganize || canDeleteData || showPrepareDownloadsUi
    : canManageClientFolders;
  const menuControls = actionMenuItem
    ? rowControls(rights, actionMenuItem, !!currentPrefix)
    : { rename: false, move: false, remove: false };

  const hasMobileDrawerContent = Boolean(
    showFilter ||
    isDbBacked ||
    (showManageControls && !!currentPrefix) ||
    (!currentPrefix && canManageClientFolders),
  );

  return (
    <Layout title="File System" content="File System" noScroll>
      <Container
        component="main"
        maxWidth={isMobile ? false : "lg"}
        disableGutters={isMobile}
        sx={{
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          position: "relative",
          width: "100%",
          boxSizing: "border-box",
          height: TABLE_PAGE_CONTAINER_HEIGHT,
          py: 3,
          px: { xs: 1.5, sm: 3 },
        }}
      >
        {isFolderSyncing && (
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              zIndex: 10,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: (t) => alpha(t.palette.common.white, 0.92),
              borderRadius: 1,
            }}
          >
            <CircularProgress size={48} sx={{ mb: 2 }} />
            <Typography variant="h6" color="text.secondary">
              Data is being prepared…
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Please wait, this may take a minute.
            </Typography>
          </Box>
        )}

        {uploadInProgress && (
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              zIndex: 30,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              pointerEvents: "auto",
              backgroundColor: (t) => alpha(t.palette.common.white, 0.55),
              backdropFilter: "blur(6px)",
              borderRadius: 1,
            }}
          >
            <Box
              sx={{
                p: 3,
                borderRadius: 1,
                backgroundColor: "background.paper",
                boxShadow: 3,
                textAlign: "center",
                width: { xs: "92%", sm: 420 },
              }}
            >
              <CircularProgress />
              <Typography variant="h6" sx={{ mt: 2 }}>
                Uploading files…
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mt: 0.5 }}
              >
                {uploadUploadedCount} / {uploadTotalCount} files
              </Typography>
            </Box>
          </Box>
        )}

        {deleteInProgress && (
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              zIndex: 40,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              pointerEvents: "auto",
              backgroundColor: (t) => alpha(t.palette.common.white, 0.55),
              backdropFilter: "blur(6px)",
              borderRadius: 1,
            }}
          >
            <Box
              sx={{
                p: 3,
                borderRadius: 1,
                backgroundColor: "background.paper",
                boxShadow: 3,
                textAlign: "center",
                width: { xs: "92%", sm: 420 },
              }}
            >
              <CircularProgress />
              <Typography variant="h6" sx={{ mt: 2 }}>
                Deleting…
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mt: 0.5 }}
              >
                Please wait, this may take a minute.
              </Typography>
            </Box>
          </Box>
        )}

        {moveInProgress && (
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              zIndex: 45,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              pointerEvents: "auto",
              backgroundColor: (t) => alpha(t.palette.common.white, 0.55),
              backdropFilter: "blur(6px)",
              borderRadius: 1,
            }}
          >
            <Box
              sx={{
                p: 3,
                borderRadius: 1,
                backgroundColor: "background.paper",
                boxShadow: 3,
                textAlign: "center",
                width: { xs: "92%", sm: 360 },
              }}
            >
              <CircularProgress />
              <Typography variant="h6" sx={{ mt: 2 }}>
                Moving file...
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mt: 0.5 }}
              >
                Please wait
              </Typography>
            </Box>
          </Box>
        )}

        <Box
          sx={{
            mb: 1.5,
            flexShrink: 0,
            flexGrow: 0,
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
            gap: { xs: 1, sm: 2 },
            px: { xs: 0, sm: 0 },
          }}
        >
          {showFilter && !isMobile && (
            <FolderTypeFilter
              value={currentFolderType}
              onChange={handleFolderTypeChange}
              disabled={!!currentPrefix}
              disabledTooltip="Filter only available at root level"
              showSftpFilter={fileSystemData?.show_sftp_filter === true}
            />
          )}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <FileSearch
              onSearch={handleSearch}
              placeholder="Search files and folders..."
              initialValue={currentSearch ?? ""}
            />
          </Box>
          {isMobile && hasMobileDrawerContent && (
            <IconButton
              color="primary"
              aria-label="Open filters and actions"
              onClick={() => setMobileFilterDrawerOpen(true)}
              sx={{
                flexShrink: 0,
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 1,
              }}
            >
              <Tune />
            </IconButton>
          )}
          {!isMobile && !currentPrefix && canManageClientFolders && (
            <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
              <ReadOnlyTooltip active={isClientViewReadOnlyMode}>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={handleOpenConnectDialog}
                  disabled={isClientViewReadOnlyMode}
                  sx={{
                    textTransform: "none",
                    height: 40,
                    borderRadius: 1,
                    px: 2,
                    fontSize: "0.85rem",
                    color: "primary.main",
                    borderColor: "rgba(0, 0, 0, 0.23)",
                    "&:hover": {
                      borderColor: "primary.main",
                      backgroundColor: "transparent",
                    },
                  }}
                >
                  Connect folder
                </Button>
              </ReadOnlyTooltip>
            </Box>
          )}
        </Box>

        <Box
          sx={{
            mb: 1.5,
            flexShrink: 0,
            flexGrow: 0,
            px: { xs: 1.5, sm: 2 },
            py: { xs: 1, sm: 1 },
            backgroundColor: "background.paper",
            borderRadius: 1,
            boxShadow: 1,
            border: "1px solid",
            borderColor: "divider",
            display: "flex",
            flexWrap: "wrap",
            alignItems: "center",
            gap: 2,
          }}
        >
          {(currentPrefix || (location.state as any)?.fromUserManagement) && (
            <IconButton
              onClick={() => {
                const fromUserManagement = (location.state as any)
                  ?.fromUserManagement;
                if (fromUserManagement) {
                  navigate(-1);
                } else {
                  handleGoBack();
                }
              }}
              size="small"
              sx={{
                width: 40,
                height: 40,
                backgroundColor: "background.paper",
                color: "primary.main",
                "&:hover": {
                  backgroundColor: "action.hover",
                },
              }}
            >
              <ArrowBack />
            </IconButton>
          )}
          <Breadcrumbs
            aria-label="breadcrumb"
            separator="/"
            sx={{
              flex: 1,
              minWidth: 0,
              overflow: "hidden",
              "& .MuiBreadcrumbs-ol": {
                flexWrap: "nowrap",
                overflow: "hidden",
              },
              "& .MuiBreadcrumbs-li": {
                maxWidth: "100%",
                overflow: "hidden",
                display: "flex",
              },
            }}
          >
            {(() => {
              const all = buildBreadcrumbs();
              const maxVisible = 3;
              const showCollapsed = all.length > maxVisible;
              const crumbs = showCollapsed
                ? [
                    all[0],
                    {
                      fname: "…",
                      fsource: "__ellipsis__",
                      isClickable: false,
                    },
                    ...all.slice(-2),
                  ]
                : all;
              return crumbs.map((crumb) =>
                crumb.fsource === "__ellipsis__" ? (
                  <Typography
                    key="ellipsis"
                    component="span"
                    color="text.secondary"
                    sx={{
                      flexShrink: 0,
                      fontWeight: "normal",
                    }}
                  >
                    …
                  </Typography>
                ) : (
                  <Link
                    key={crumb.fsource}
                    underline="hover"
                    color={crumb.isClickable ? "inherit" : "text.primary"}
                    onClick={(e) => {
                      e.preventDefault();
                      if (crumb.isClickable) {
                        handleBreadcrumbClick(crumb.fsource);
                      }
                    }}
                    sx={{
                      cursor: crumb.isClickable ? "pointer" : "default",
                      display: "flex",
                      alignItems: "center",
                      gap: 0.5,
                      fontWeight: crumb.isClickable ? "normal" : "bold",
                      minWidth: 0,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      "&:hover": {
                        color: crumb.isClickable
                          ? "primary.main"
                          : "text.primary",
                      },
                    }}
                  >
                    {crumb.fsource === "" && (
                      <Home fontSize="small" sx={{ flexShrink: 0 }} />
                    )}
                    <span
                      style={{ overflow: "hidden", textOverflow: "ellipsis" }}
                    >
                      {crumb.fname}
                    </span>
                  </Link>
                ),
              );
            })()}
          </Breadcrumbs>
          {!isMobile &&
            (isDbBacked || (showManageControls && currentPrefix)) && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "stretch",
                  gap: 1,
                  height: 40,
                  flexBasis: { xs: "100%", sm: "auto" },
                  minWidth: 0,
                }}
              >
                {showManageControls && currentPrefix && (
                  <Box sx={{ display: "flex", gap: 1, alignItems: "stretch" }}>
                    <>
                      <ReadOnlyTooltip active={isClientViewReadOnlyMode}>
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<CreateNewFolder fontSize="small" />}
                          onClick={handleCreateFolderClick}
                          disabled={isClientViewReadOnlyMode}
                          sx={{
                            height: "100%",
                            textTransform: "none",
                            fontSize: "0.8rem",
                            px: 1.5,
                            borderRadius: 1,
                          }}
                        >
                          New folder
                        </Button>
                      </ReadOnlyTooltip>
                      <ReadOnlyTooltip active={isClientViewReadOnlyMode}>
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<UploadFile fontSize="small" />}
                          onClick={handleUploadButtonClick}
                          disabled={isClientViewReadOnlyMode}
                          sx={{
                            height: "100%",
                            textTransform: "none",
                            fontSize: "0.8rem",
                            px: 1.5,
                            borderRadius: 1,
                          }}
                        >
                          Upload file
                        </Button>
                      </ReadOnlyTooltip>
                      {showPrepareDownloadsUi &&
                        currentPrefix &&
                        topDocumentZipTargetsOnPage.length > 0 && (
                          <Tooltip
                            title={
                              isClientViewReadOnlyMode
                                ? clientViewDisabledTooltip
                                : fileSystemData?.sync_status === "syncing"
                                  ? "Wait until folder sync finishes."
                                  : topZipJobCount > 0
                                    ? "Wait until the current downloads finish preparing."
                                    : "Prepare downloads for the document folders on this page that aren't ready yet."
                            }
                          >
                            <span>
                              <Button
                                size="small"
                                variant="outlined"
                                color="secondary"
                                startIcon={<Inventory2 fontSize="small" />}
                                onClick={() =>
                                  setPregenerateZipDialogOpen(true)
                                }
                                disabled={
                                  isClientViewReadOnlyMode ||
                                  !someTopFolderZipNotCached ||
                                  fileSystemData?.sync_status === "syncing" ||
                                  topZipJobCount > 0
                                }
                                sx={{
                                  height: "100%",
                                  textTransform: "none",
                                  fontSize: "0.8rem",
                                  px: 1.5,
                                  borderRadius: 1,
                                }}
                              >
                                Prepare Downloads
                              </Button>
                            </span>
                          </Tooltip>
                        )}
                    </>
                  </Box>
                )}
                {canViewFileTimestamps && currentPrefix && (
                  <ToggleButtonGroup
                    value={timeDisplayMode}
                    exclusive
                    onChange={(
                      _event: React.MouseEvent<HTMLElement>,
                      value: TimeDisplayMode | null,
                    ) => {
                      if (value != null) {
                        setTimeDisplayMode(value);
                        updateURL({ timeDisplay: value });
                      }
                    }}
                    size="small"
                    sx={{
                      height: "100%",
                      "& .MuiToggleButton-root": { py: 0 },
                    }}
                  >
                    <ToggleButton value="local" aria-label="Local time">
                      Local
                    </ToggleButton>
                    <ToggleButton value="utc" aria-label="UTC">
                      UTC
                    </ToggleButton>
                  </ToggleButtonGroup>
                )}
                <FormControl
                  size="small"
                  sx={{
                    minWidth: 170,
                    height: "100%",
                    "& .MuiInputBase-root": { height: "100%" },
                  }}
                >
                  <Select<string>
                    value={sortSelectValue}
                    onChange={(e: SelectChangeEvent<string>) => {
                      const next = String(e.target.value ?? sortSelectValue);
                      handleSortChange(next);
                    }}
                    sx={{ fontSize: "0.85rem", height: "100%" }}
                  >
                    {canViewFileTimestamps
                      ? [
                          <MenuItem
                            key="date_desc"
                            value="date_desc"
                            disabled={!fileSystemData?.has_direct_files}
                          >
                            Date (Newest first)
                          </MenuItem>,
                          <MenuItem
                            key="date_asc"
                            value="date_asc"
                            disabled={!fileSystemData?.has_direct_files}
                          >
                            Date (Oldest first)
                          </MenuItem>,
                        ]
                      : null}
                    <MenuItem
                      value="size_desc"
                      disabled={!fileSystemData?.has_direct_files}
                    >
                      Size (Largest first)
                    </MenuItem>
                    <MenuItem
                      value="size_asc"
                      disabled={!fileSystemData?.has_direct_files}
                    >
                      Size (Smallest first)
                    </MenuItem>
                    <MenuItem value="name_asc">Name (A → Z)</MenuItem>
                    <MenuItem value="name_desc">Name (Z → A)</MenuItem>
                  </Select>
                </FormControl>
              </Box>
            )}
        </Box>

        <Box
          sx={{
            flex: "1 1 0",
            minHeight: 0,
            minWidth: 0,
            display: "flex",
            flexDirection: "column",
            backgroundColor: "background.paper",
            borderRadius: 1,
            boxShadow: 1,
            border: "1px solid",
            borderColor: "divider",
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              flex: "1 1 0",
              minHeight: 0,
              minWidth: 0,
              display: "flex",
              flexDirection: "column",
            }}
          >
            <FileList
              showFileDate={canViewFileTimestamps}
              timeDisplayMode={timeDisplayMode}
              hideEmptyLabel={currentPrefix === ""}
              items={displayedFolders}
              loading={fileSystemLoading || fileSystemFetching}
              error={fileSystemError ? "Failed to load files" : null}
              currentLimit={currentLimit}
              onLimitChange={handleLimitChange}
              onItemClick={handleItemClick}
              onDownloadClick={handleDownloadClick}
              topDocumentFolderRootPrefix={fileSystemData?.client_folder_prefix}
              mayDownloadTopDocumentFolder={effectiveMayDownloadTopFolder}
              disableTopFolderZipDuringSync={
                fileSystemData?.sync_status === "syncing"
              }
              showAdminTopFolderZipCacheBadge={showPrepareDownloadsUi}
              adminTopFolderZipCacheBySource={topFolderZipReadyMap}
              onPreviewClick={handlePreviewClick}
              onMoreOptionsClick={
                showRowMenu ? handleOpenActionMenu : undefined
              }
              showDownloadButton={true}
              showPreviewButton={true}
              disableFileClick={true}
              showMoreOptions={showRowMenu}
              disableMoreOptions={isClientViewReadOnlyMode}
              moreOptionsDisabledTooltip={clientViewDisabledTooltip}
              moreOptionsFoldersOnly={!currentPrefix}
              showPagination={true}
              size="medium"
              variant="default"
              maxHeight="100%"
              currentPage={currentPage}
              totalCount={fileSystemData?.total_count}
              totalPages={
                isDbBacked
                  ? fileSystemData.total_pages!
                  : fileSystemData?.continuation_token
                    ? pageTokens.length + 1
                    : currentPage
              }
              onPageChange={handlePageChange}
              pageTokens={pageTokens}
              downloadingItems={downloadManager.downloadingItems}
              downloadProgress={downloadManager.downloadProgress}
              hasNextPage={
                isDbBacked
                  ? currentPage < (fileSystemData?.total_pages || 1)
                  : !!fileSystemData?.continuation_token
              }
            />
          </Box>
        </Box>

        <Drawer
          anchor="bottom"
          open={isMobile && mobileFilterDrawerOpen}
          onClose={() => setMobileFilterDrawerOpen(false)}
          PaperProps={{
            sx: {
              borderTopLeftRadius: 2,
              borderTopRightRadius: 2,
              maxHeight: "min(90vh, 640px)",
              px: 2,
              py: 2,
            },
          }}
        >
          <Typography variant="subtitle1" fontWeight={800} sx={{ mb: 2 }}>
            Filters and actions
          </Typography>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 2,
              pb: 1,
            }}
          >
            {showFilter && (
              <Box>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  fontWeight={700}
                  sx={{ display: "block", mb: 1 }}
                >
                  Folder type
                </Typography>
                <FolderTypeFilter
                  value={currentFolderType}
                  onChange={(v) => {
                    handleFolderTypeChange(v);
                    setMobileFilterDrawerOpen(false);
                  }}
                  disabled={!!currentPrefix}
                  disabledTooltip="Filter only available at root level"
                  showSftpFilter={fileSystemData?.show_sftp_filter === true}
                />
              </Box>
            )}

            {!currentPrefix && canManageClientFolders && (
              <>
                {showFilter && <Divider />}
                <Box>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    fontWeight={700}
                    sx={{ display: "block", mb: 1 }}
                  >
                    Folders
                  </Typography>
                  <ReadOnlyTooltip active={isClientViewReadOnlyMode}>
                    <Button
                      fullWidth
                      size="medium"
                      variant="outlined"
                      onClick={() => {
                        handleOpenConnectDialog();
                        setMobileFilterDrawerOpen(false);
                      }}
                      disabled={isClientViewReadOnlyMode}
                      sx={{ textTransform: "none", fontWeight: 600 }}
                    >
                      Connect folder
                    </Button>
                  </ReadOnlyTooltip>
                </Box>
              </>
            )}

            {(isDbBacked || (showManageControls && currentPrefix)) && (
              <>
                {(showFilter || (!currentPrefix && canManageClientFolders)) && (
                  <Divider sx={{ my: 0.5 }} />
                )}
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 2,
                  }}
                >
                  {showManageControls && currentPrefix && (
                    <Box
                      sx={{ display: "flex", flexDirection: "column", gap: 1 }}
                    >
                      <>
                        <ReadOnlyTooltip active={isClientViewReadOnlyMode}>
                          <Button
                            fullWidth
                            size="medium"
                            variant="outlined"
                            startIcon={<CreateNewFolder fontSize="small" />}
                            onClick={() => {
                              handleCreateFolderClick();
                              setMobileFilterDrawerOpen(false);
                            }}
                            disabled={isClientViewReadOnlyMode}
                            sx={{ textTransform: "none", fontWeight: 600 }}
                          >
                            New folder
                          </Button>
                        </ReadOnlyTooltip>
                        <ReadOnlyTooltip active={isClientViewReadOnlyMode}>
                          <Button
                            fullWidth
                            size="medium"
                            variant="outlined"
                            startIcon={<UploadFile fontSize="small" />}
                            onClick={() => {
                              handleUploadButtonClick();
                              setMobileFilterDrawerOpen(false);
                            }}
                            disabled={isClientViewReadOnlyMode}
                            sx={{ textTransform: "none", fontWeight: 600 }}
                          >
                            Upload file
                          </Button>
                        </ReadOnlyTooltip>
                      </>
                    </Box>
                  )}
                  {canViewFileTimestamps && currentPrefix && (
                    <ToggleButtonGroup
                      fullWidth
                      value={timeDisplayMode}
                      exclusive
                      onChange={(
                        _event: React.MouseEvent<HTMLElement>,
                        value: TimeDisplayMode | null,
                      ) => {
                        if (value != null) {
                          setTimeDisplayMode(value);
                          updateURL({ timeDisplay: value });
                        }
                      }}
                      size="small"
                    >
                      <ToggleButton value="local" aria-label="Local time">
                        Local time
                      </ToggleButton>
                      <ToggleButton value="utc" aria-label="UTC">
                        UTC
                      </ToggleButton>
                    </ToggleButtonGroup>
                  )}
                  <FormControl fullWidth size="small">
                    <Select<string>
                      value={sortSelectValue}
                      onChange={(e: SelectChangeEvent<string>) => {
                        const next = String(e.target.value ?? sortSelectValue);
                        handleSortChange(next);
                        setMobileFilterDrawerOpen(false);
                      }}
                      sx={{ fontSize: "0.9rem" }}
                    >
                      {canViewFileTimestamps
                        ? [
                            <MenuItem
                              key="date_desc"
                              value="date_desc"
                              disabled={!fileSystemData?.has_direct_files}
                            >
                              Date (Newest first)
                            </MenuItem>,
                            <MenuItem
                              key="date_asc"
                              value="date_asc"
                              disabled={!fileSystemData?.has_direct_files}
                            >
                              Date (Oldest first)
                            </MenuItem>,
                          ]
                        : null}
                      <MenuItem
                        value="size_desc"
                        disabled={!fileSystemData?.has_direct_files}
                      >
                        Size (Largest first)
                      </MenuItem>
                      <MenuItem
                        value="size_asc"
                        disabled={!fileSystemData?.has_direct_files}
                      >
                        Size (Smallest first)
                      </MenuItem>
                      <MenuItem value="name_asc">Name (A → Z)</MenuItem>
                      <MenuItem value="name_desc">Name (Z → A)</MenuItem>
                    </Select>
                  </FormControl>
                </Box>
              </>
            )}
          </Box>
        </Drawer>
      </Container>

      <input
        ref={fileInputRef}
        type="file"
        hidden
        multiple
        onChange={handleUploadInputChange}
      />

      <Menu
        anchorEl={actionMenuAnchor}
        open={Boolean(actionMenuAnchor && actionMenuItem)}
        onClose={handleCloseActionMenu}
        transitionDuration={0}
      >
        {menuControls.rename && (
          <MenuItem
            onClick={
              isClientViewReadOnlyMode ? undefined : handleRenameDialogOpen
            }
            disabled={isClientViewReadOnlyMode}
          >
            <DriveFileRenameOutline fontSize="small" sx={{ mr: 1 }} />
            Rename folder
          </MenuItem>
        )}
        {actionMenuItem?.ftype === "folder" &&
          showPrepareDownloadsUi &&
          !!fileSystemData?.client_folder_prefix &&
          isTopDocumentFolderDownloadTarget(
            actionMenuItem,
            fileSystemData.client_folder_prefix,
          ) &&
          topFolderZipReadyMap[normPath(actionMenuItem.fsource)] !== true && (
            <MenuItem
              onClick={
                isClientViewReadOnlyMode
                  ? undefined
                  : () => void handleGenerateTopFolderZipFromMenu()
              }
              disabled={
                isClientViewReadOnlyMode ||
                fileSystemData?.sync_status === "syncing"
              }
            >
              <Inventory2 fontSize="small" sx={{ mr: 1 }} />
              Prepare Downloads
            </MenuItem>
          )}
        {menuControls.move && (
          <MenuItem
            onClick={
              isClientViewReadOnlyMode ? undefined : handleMoveDialogOpen
            }
            disabled={moveInProgress || isClientViewReadOnlyMode}
          >
            <DriveFileMoveOutlined fontSize="small" sx={{ mr: 1 }} />
            Move file
          </MenuItem>
        )}
        {menuControls.remove && (
          <MenuItem
            onClick={
              isClientViewReadOnlyMode ? undefined : handleDeleteDialogOpen
            }
            disabled={isClientViewReadOnlyMode}
          >
            <Delete fontSize="small" sx={{ mr: 1 }} />
            {currentPrefix ? "Delete" : "Remove folder"}
          </MenuItem>
        )}
      </Menu>

      <AppDialog
        open={pregenerateZipDialogOpen}
        onClose={() => setPregenerateZipDialogOpen(false)}
        title="Prepare Downloads"
        maxWidth="sm"
        actions={
          <>
            <Button onClick={() => setPregenerateZipDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={() => void handleConfirmBulkPregenerateTopZips()}
              variant="contained"
              color="secondary"
            >
              Prepare Downloads
            </Button>
          </>
        }
      >
        <Typography variant="body2" sx={{ mb: 1.5 }}>
          This prepares downloads for every{" "}
          <strong>document-type folder</strong> on this page that isn&rsquo;t
          ready yet.
        </Typography>
        <Typography variant="body2" sx={{ mb: 1.5 }}>
          A prepared download can be reused for up to <strong>one year</strong>{" "}
          while the folder contents match the snapshot. If files are added,
          removed, or changed, it is treated as out of date and must be prepared
          again.
        </Typography>
        <Typography variant="body2" color="text.secondary">
          You will get a short summary when the batch is queued. Individual jobs
          may take time; progress appears in the activity area like a normal
          download.
        </Typography>
      </AppDialog>

      <AppDialog
        open={renameDialogOpen}
        onClose={handleRenameDialogClose}
        title={
          actionMenuItem?.ftype === "folder" ? "Rename folder" : "Rename file"
        }
        maxWidth="xs"
        actions={
          <>
            <Button
              onClick={handleRenameDialogClose}
              disabled={renameSubmitting}
            >
              Cancel
            </Button>
            <Button
              onClick={handleRenameConfirm}
              variant="contained"
              disabled={renameSubmitting}
            >
              {renameSubmitting ? "Renaming..." : "Rename"}
            </Button>
          </>
        }
      >
        {isRootFolderRenameDialog &&
        (actionMenuItem?.root_list_owner_prefix || "") !== "" ? (
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              alignItems: "center",
              gap: 0.75,
              mt: 0.5,
            }}
          >
            <Typography
              component="span"
              sx={{
                color: "text.secondary",
                flexShrink: 0,
                fontSize: "1rem",
                userSelect: "none",
              }}
            >
              {actionMenuItem.root_list_owner_prefix}
            </Typography>
            <TextField
              autoFocus
              margin="dense"
              label="Folder name"
              value={renameValue}
              onChange={(e) => setRenameValue(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  void handleRenameConfirm();
                }
              }}
              sx={{ flex: "1 1 140px", minWidth: 0, maxWidth: "100%" }}
            />
          </Box>
        ) : (
          <>
            <TextField
              autoFocus
              margin="dense"
              fullWidth
              label="New name"
              value={renameValue}
              disabled={renameSubmitting}
              onChange={(e) => setRenameValue(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  if (!renameSubmitting) void handleRenameConfirm();
                }
              }}
            />
            {renameMovesStorage && actionMenuItem ? (
              <Box sx={{ mt: 1 }}>
                {physicalFolderName(actionMenuItem.fsource) !==
                actionMenuItem.fname ? (
                  <Typography variant="body2" color="text.secondary">
                    Stored as: {physicalFolderName(actionMenuItem.fsource)}
                  </Typography>
                ) : null}
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mt: 0.5 }}
                >
                  Bot jobs read this folder as an employee folder, so the rename
                  moves its files in storage and the bots see the new name.
                  Upload jobs that already built their Matches need them deleted
                  before the next run. Moved files show the rename date as their
                  last modified date.
                </Typography>
              </Box>
            ) : null}
          </>
        )}
      </AppDialog>

      <MoveFileDialog
        open={moveDialogOpen}
        onClose={handleMoveDialogClose}
        file={actionMenuItem?.ftype === "file" ? actionMenuItem : null}
        scopePrefix={currentPrefix}
        moveInProgress={moveInProgress}
        onMoveHere={handleMoveHere}
      />

      <AppDialog
        open={uploadDialogOpen}
        onClose={() => {
          setUploadDialogOpen(false);
          setUploadDraftFiles([]);
          setUploadDragOver(false);
        }}
        title="Upload file(s)"
        maxWidth="sm"
        showCloseIcon
        actions={
          <>
            <Button
              onClick={() => {
                setUploadDialogOpen(false);
                setUploadDraftFiles([]);
                setUploadDragOver(false);
              }}
              disabled={uploadInProgress}
            >
              Cancel
            </Button>
            <Button
              onClick={() => void handleConfirmUpload()}
              variant="contained"
              disabled={
                uploadInProgress ||
                uploadDraftFiles.length === 0 ||
                uploadDraftFiles.every((d) => !!d.error)
              }
            >
              {uploadInProgress ? (
                <CircularProgress size={18} color="inherit" />
              ) : (
                "Upload"
              )}
            </Button>
          </>
        }
      >
        <Box
          sx={{
            border: "2px dashed",
            borderColor: uploadDragOver ? "primary.main" : "divider",
            borderRadius: 1,
            p: 2,
            textAlign: "center",
            cursor: uploadInProgress ? "default" : "pointer",
            bgcolor: uploadDragOver ? "action.hover" : "background.paper",
          }}
          onClick={() => {
            if (uploadInProgress) return;
            fileInputRef.current?.click();
          }}
          onDragEnter={(e) => {
            e.preventDefault();
            if (uploadInProgress) return;
            setUploadDragOver(true);
          }}
          onDragOver={(e) => {
            e.preventDefault();
            if (uploadInProgress) return;
            setUploadDragOver(true);
          }}
          onDragLeave={(e) => {
            e.preventDefault();
            setUploadDragOver(false);
          }}
          onDrop={(e) => {
            e.preventDefault();
            e.stopPropagation();
            if (uploadInProgress) return;
            setUploadDragOver(false);
            const files = Array.from(e.dataTransfer.files || []);
            if (!files.length) return;
            void addFilesToUploadDraft(files);
          }}
        >
          <Typography variant="body2" sx={{ fontWeight: 700 }}>
            Drag and drop files here
          </Typography>
          <Typography variant="caption" color="text.secondary">
            or click to choose
          </Typography>
        </Box>

        {uploadDraftFiles.length > 0 && (
          <Box sx={{ mt: 2, maxHeight: 260, overflowY: "auto" }}>
            {uploadDraftFiles.map((d) => (
              <Box
                key={d.id}
                sx={{
                  display: "flex",
                  alignItems: "flex-start",
                  justifyContent: "space-between",
                  gap: 2,
                  py: 1,
                  borderBottom: "1px solid",
                  borderColor: "divider",
                }}
              >
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <TextField
                      size="small"
                      fullWidth
                      value={d.baseName}
                      disabled={uploadInProgress}
                      onChange={(e) => {
                        const nextBaseName = e.target.value;
                        setUploadDraftFiles((prev) => {
                          const updated = prev.map((item) =>
                            item.id === d.id
                              ? { ...item, baseName: nextBaseName }
                              : item,
                          );
                          return validateUploadDrafts(updated);
                        });
                      }}
                    />
                    {!!d.extension && (
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ flexShrink: 0 }}
                      >
                        {d.extension}
                      </Typography>
                    )}
                  </Box>
                  {d.error ? (
                    <Typography variant="caption" color="error.main">
                      {d.error}
                    </Typography>
                  ) : (
                    <Typography variant="caption" color="success.main">
                      Ready
                    </Typography>
                  )}
                </Box>
                <Typography variant="caption" color="text.secondary">
                  {formatBytes(d.file.size)}
                </Typography>
              </Box>
            ))}
          </Box>
        )}

        {uploadDraftFiles.length === 0 && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            Select one or more files. If a file with the same name already
            exists in this folder, it will be marked as not uploadable.
          </Typography>
        )}
      </AppDialog>

      <AppDialog
        open={createFolderDialogOpen}
        onClose={handleCreateFolderDialogClose}
        title="New folder"
        maxWidth="xs"
        actions={
          <>
            <Button onClick={handleCreateFolderDialogClose}>Cancel</Button>
            <Button onClick={handleCreateFolderConfirm} variant="contained">
              Create
            </Button>
          </>
        }
      >
        <TextField
          autoFocus
          margin="dense"
          fullWidth
          label="Folder name"
          value={createFolderName}
          onChange={(e) => setCreateFolderName(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              void handleCreateFolderConfirm();
            }
          }}
        />
      </AppDialog>

      <ConnectFolderDialog
        open={connectDialogOpen}
        onClose={handleCloseConnectDialog}
        title="Connect folder"
        onConnected={(created, folderName) => {
          dispatch(
            startFolderConnectSync({
              folderId: created.folder_id,
              folderName: created.folder || folderName,
            }),
          );
        }}
        onError={(message) =>
          dispatch(
            pushNotification({
              message,
              severity: "error",
            }),
          )
        }
      />

      <AppDialog
        open={deleteDialogOpen && !deleteInProgress}
        onClose={handleDeleteDialogClose}
        title={
          !currentPrefix &&
          actionMenuItem?.ftype === "folder" &&
          typeof actionMenuItem.user_folder_id === "number"
            ? "Remove folder"
            : actionMenuItem?.ftype === "folder"
              ? "Delete folder"
              : "Delete file"
        }
        maxWidth="xs"
        actions={
          <>
            <Button
              onClick={handleDeleteDialogClose}
              disabled={deleteInProgress}
            >
              Cancel
            </Button>
            <Button
              onClick={() => {
                void handleDeleteSelected();
              }}
              color="error"
              variant="contained"
              disabled={deleteInProgress}
            >
              {deleteInProgress ? (
                <CircularProgress size={18} color="inherit" />
              ) : !currentPrefix &&
                actionMenuItem?.ftype === "folder" &&
                typeof actionMenuItem.user_folder_id === "number" ? (
                "Remove"
              ) : (
                "Delete"
              )}
            </Button>
          </>
        }
      >
        {!currentPrefix &&
        actionMenuItem?.ftype === "folder" &&
        typeof actionMenuItem.user_folder_id === "number" ? (
          <Box>
            <Alert severity="warning" sx={{ mb: 2.5 }}>
              Objects in S3 under this path are not deleted. Only the platform
              link and synced records in the database are removed.
            </Alert>
            <Typography sx={{ mb: 1.75 }}>
              Remove &quot;{actionMenuItem.fname}&quot; from the platform?
            </Typography>
            <Typography variant="body2" color="text.secondary">
              The folder link and sync cache in the database will be removed.
            </Typography>
          </Box>
        ) : (
          <Typography>
            {actionMenuItem?.ftype === "folder"
              ? `Are you sure you want to delete folder "${actionMenuItem?.fname}"?`
              : `Are you sure you want to delete file "${actionMenuItem?.fname}"?`}
          </Typography>
        )}
      </AppDialog>
    </Layout>
  );
}
